/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo, useRef } from 'react'
import { DragDropProvider } from '@dnd-kit/react'
import { isSortable } from '@dnd-kit/react/sortable'
import {
    PointerSensor,
    PointerActivationConstraints,
    AutoScroller,
} from '@dnd-kit/dom'
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers'
import { arrayMove } from '@dnd-kit/helpers'
import { PlayerRow, StatPickerDraggableProps } from './utils/types.ts'
import { groupStatSelects } from './utils/stat-groupings.ts'
import { ROW_HEIGHT, HEADER_HEIGHT } from './utils/constants.ts'

import StatPickerDraggableElement from './StatPickerDraggableElement.tsx'
import { useDimensions } from './utils/responsive-dimensions.tsx'

export default function StatPickerDraggable({
    draftSelected,
    setDraftSelected,
    existingDraftTypes,
    currentType,
    handleToggleStat,
}: StatPickerDraggableProps) {
    type DragOverEvent = Parameters<
        NonNullable<React.ComponentProps<typeof DragDropProvider>['onDragOver']>
    >[0]

    const groupRefs = useRef<Record<string, SVGGElement | null>>({})

    const groupedDraft = groupStatSelects(
        draftSelected as (keyof PlayerRow)[],
        currentType
    )

    const { isMobile } = useDimensions()

    const rightSideSvgHeight =
        draftSelected.length * ROW_HEIGHT +
        existingDraftTypes.length * HEADER_HEIGHT +
        10

    const handleDragOver = (event: DragOverEvent) => {
        const { operation } = event
        const { source, target } = operation
        if (isSortable(source) && isSortable(target)) {
            const overIndex = target.sortable.index
            const activeIndex = source.sortable.index
            if (activeIndex !== overIndex) {
                setDraftSelected((prev) =>
                    arrayMove([...prev], activeIndex, overIndex)
                )
            }
        }
    }

    const sensors = useMemo(
        () => [
            PointerSensor.configure({
                activationConstraints: [
                    new PointerActivationConstraints.Distance({ value: 8 }),
                    new PointerActivationConstraints.Delay({
                        value: isMobile ? 800 : 200,
                        tolerance: isMobile ? 5 : 10,
                    }),
                ],
            }),
        ],
        []
    )

    return (
        <div className="stat-picker-right-side-wrapper">
            <label className="stat-picker-right-side-title">
                Selected Stats (drag to reorder)
            </label>
            <div className="stat-picker-right-side-scroll">
                <DragDropProvider
                    sensors={sensors}
                    onDragOver={handleDragOver}
                    modifiers={[RestrictToVerticalAxis]}
                    plugins={(defaults) =>
                        defaults.filter((plugin) => plugin !== AutoScroller)
                    }
                >
                    <svg
                        style={{
                            width: '100%',
                            height: `${rightSideSvgHeight}px`,
                        }}
                    >
                        {(() => {
                            let currentY = 5
                            return existingDraftTypes.map((type) => {
                                const groupStats = groupedDraft[type]
                                if (groupStats.length === 0) {
                                    return null
                                }

                                const headerY = currentY
                                currentY += HEADER_HEIGHT

                                const groupStartY = currentY

                                const statNodes = groupStats.map((stat, i) => (
                                    <StatPickerDraggableElement
                                        key={stat}
                                        stat={stat}
                                        draftSelected={draftSelected}
                                        type={type}
                                        index={i}
                                        groupRefs={groupRefs}
                                        currentType={currentType}
                                        handleToggleStat={handleToggleStat}
                                    />
                                ))

                                currentY += groupStats.length * ROW_HEIGHT

                                let sectionTitle = type as string
                                if (type === 'batting') {
                                    sectionTitle = 'BATTING/PITCHING'
                                } else if (type === 'pitch_arsenal') {
                                    sectionTitle = 'PITCH ARSENAL'
                                }

                                return (
                                    <g key={`draft-group-${type}`}>
                                        <text
                                            x={8}
                                            y={headerY + 16}
                                            className="stat-picker-drag-header-text"
                                        >
                                            {sectionTitle}
                                        </text>

                                        <g
                                            ref={(el) => {
                                                groupRefs.current[type] = el
                                            }}
                                            transform={`translate(0, ${groupStartY})`}
                                        >
                                            {statNodes}
                                        </g>
                                    </g>
                                )
                            })
                        })()}
                    </svg>
                </DragDropProvider>
            </div>
        </div>
    )
}
